import { useMemo } from 'react';

import { PARTNER_TASK_CARDS } from '#client/components/PartnerDash/PartnerNextUp/constants';
import { RA_TASK_TYPES } from '#client/constants/partnerTasks';
import useCheckoutComplete from '#client/hooks/useCheckoutComplete';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';

const SEQUENCE = ['llc_formation', 'change_of_registered_agent', 'ein_creation'];
const NO_ACTION_NEEDED_STATUSES = ['complete', 'active', 'declined', 'cancelled', 'pending', 'processing'];

function usePartnerNextUp() {
  const { loading, orderTasksWithStatus } = useOrderTrackerStatus();
  const { getCheckoutCompleteUrl } = useCheckoutComplete();

  const cards = useMemo(() => {
    if (loading) {
      return [];
    }

    const taskMap = orderTasksWithStatus.reduce((acc, task) => {
      acc[task.type] = task;
      return acc;
    }, {});

    const resolveTask = (type) => {
      if (type === 'change_of_registered_agent') {
        return RA_TASK_TYPES.map((t) => taskMap[t]).find(Boolean);
      }
      return taskMap[type];
    };

    const isNoActionNeeded = (task) => task && NO_ACTION_NEEDED_STATUSES.includes(task.status);

    const activeIndex = SEQUENCE.findIndex((type) => !isNoActionNeeded(resolveTask(type)));

    if (activeIndex === -1) {
      return [];
    }

    const llcNoActionNeeded = isNoActionNeeded(resolveTask('llc_formation'));

    return SEQUENCE.slice(activeIndex).map((type, idx) => {
      const task = resolveTask(type);
      const baseCard = { type, isLocked: idx > 0, ...PARTNER_TASK_CARDS[type] };

      if (type === 'ein_creation' && task?.status === 'incomplete' && llcNoActionNeeded) {
        const { infoNeeded } = PARTNER_TASK_CARDS.ein_creation;
        return {
          ...baseCard,
          isLocked: false,
          titleId: infoNeeded.titleId,
          descriptionId: infoNeeded.descriptionId,
          imageUrl: infoNeeded.imageUrl,
          ctaUrl: getCheckoutCompleteUrl('ein_creation') || '#'
        };
      }

      return baseCard;
    });
  }, [loading, orderTasksWithStatus, getCheckoutCompleteUrl]);

  return { cards, loading };
}

export default usePartnerNextUp;
